import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AnswersDataService } from '@services/answers-data.service';
import { ProfileStepCode } from '@shared/models/profile-step-code';
import { ApiException } from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';

@Injectable({ providedIn: 'root' })
export class ResumeAuthService {
  private pendingAuthorizedReturnUrl: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly customerService: CustomerService,
    private readonly answersDataService: AnswersDataService
  ) {}

  async navigateAfterAuthorization(userId: string, returnUrl?: string | null): Promise<void> {
    const trimmedUserId = userId.trim();
    const userProfile = await this.customerService.getUserData(trimmedUserId);

    if (!userProfile) {
      throw new Error('Profile not found');
    }

    this.answersDataService.initData(userProfile);

    const safeReturnUrl = this.getSafeReturnUrl(returnUrl);

    if (safeReturnUrl) {
      this.pendingAuthorizedReturnUrl = safeReturnUrl;
      await this.router.navigateByUrl(safeReturnUrl);
      return;
    }

    this.pendingAuthorizedReturnUrl = null;

    const step = this.answersDataService.getLatestStepRoute();

    if (step === ProfileStepCode.MyProfileStepSummary) {
      await this.router.navigate(['my-profile/steps'], {
        queryParams: { userId: trimmedUserId },
        state: { showRecommendation: true }
      });
      return;
    }

    await this.router.navigate([step], {
      queryParams: { userId: trimmedUserId }
    });
  }

  redirectToResume(userId?: string | null, returnUrl?: string | null): Promise<boolean> {
    const trimmedUserId = userId?.trim() ?? '';
    const safeReturnUrl = this.getSafeReturnUrl(returnUrl);

    return this.router.navigate(['mif-login'], {
      queryParams: {
        ...(trimmedUserId ? { userId: trimmedUserId } : {}),
        ...(safeReturnUrl ? { returnUrl: safeReturnUrl } : {})
      }
    });
  }

  isUnauthorizedError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 401;
    }

    if (ApiException.isApiException(error)) {
      return error.status === 401;
    }

    if (this.isObject(error) && typeof error.status === 'number') {
      return error.status === 401;
    }

    return false;
  }

  isAuthEntryUrl(url: string | null | undefined): boolean {
    if (!url) {
      return false;
    }

    const path = url.split('?')[0];

    return path === '/resume' || path === '/mif-login';
  }

  consumeAuthorizedReturn(url: string | null | undefined): boolean {
    const safeUrl = this.getSafeReturnUrl(url);

    if (!safeUrl || this.pendingAuthorizedReturnUrl !== safeUrl) {
      return false;
    }

    this.pendingAuthorizedReturnUrl = null;
    return true;
  }

  private getSafeReturnUrl(returnUrl?: string | null): string | null {
    if (!returnUrl) {
      return null;
    }

    const trimmedReturnUrl = returnUrl.trim();

    if (!trimmedReturnUrl.startsWith('/') || trimmedReturnUrl.startsWith('//')) {
      return null;
    }

    if (this.isAuthEntryUrl(trimmedReturnUrl)) {
      return null;
    }

    return trimmedReturnUrl;
  }

  private isObject(value: unknown): value is { status?: unknown } {
    return typeof value === 'object' && value !== null;
  }
}
